import {
  buildStixBundleFromGraph,
  resolveStixExportGraph
} from '@/lib/stix-from-graph';
import { cn } from '@/lib/utils';
import { Download } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { type IGraph, graphState } from '@chainlit/react-client';

import { graphHistoryState } from '@/state/graphHistory';
import {
  graphSelectionState,
  selectedSnapshotIndexState
} from '@/state/graphSelection';

function downloadJson(name: string, contents: string) {
  const blob = new Blob([contents], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Tab-bar action that downloads the STIX bundle for the currently displayed
 * graph/snapshot, rebuilt client-side from its nodes/edges (see
 * `resolveStixExportGraph` / `buildStixBundleFromGraph`). Rendered right of the
 * OPERATIONS/COA/STIX selectors; self-hides whenever the displayed snapshot has
 * no STIX (a COA-only view, or early in the operation before STIX exists).
 */
export default function StixDownloadButton() {
  const graphs = useRecoilValue<Record<string, IGraph>>(graphState);
  const graphHistory = useRecoilValue(graphHistoryState);
  const selectedGraphId = useRecoilValue(graphSelectionState);
  const selectedSnapshotIndex = useRecoilValue(selectedSnapshotIndexState);

  const stix = useMemo(
    () =>
      resolveStixExportGraph({
        graphs,
        graphHistory,
        selectedGraphId,
        selectedSnapshotIndex
      }),
    [graphs, graphHistory, selectedGraphId, selectedSnapshotIndex]
  );

  const handleDownload = useCallback(() => {
    if (!stix) return;
    const bundle = buildStixBundleFromGraph(stix.nodes, stix.edges);
    const stamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    downloadJson(`${stamp}_stix.json`, JSON.stringify(bundle, null, 2));
  }, [stix]);

  if (!stix) return null;

  return (
    <div className="ml-1 flex items-center gap-1.5">
      <span aria-hidden="true" className="h-4 w-px shrink-0 bg-border" />
      <button
        type="button"
        data-no-maximize
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onClick={handleDownload}
        className={cn(
          'flex h-5 items-center gap-1 rounded border border-border px-1.5 text-[10px] font-medium uppercase tracking-wide transition-all',
          'text-muted-foreground',
          'hover:border-primary hover:bg-primary hover:text-primary-foreground',
          'active:scale-95 active:bg-primary/80',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary'
        )}
        title="Download STIX bundle"
        aria-label="Download STIX bundle"
      >
        <Download className="h-3 w-3" aria-hidden="true" />
        <span>Export STIX</span>
      </button>
    </div>
  );
}
